import {
  Button,
  Form,
  FormProps,
  Modal,
  ModalProps,
  Space,
  Steps,
  notification
} from 'antd';
import { CrudFilters, HttpError, useList } from '@refinedev/core';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ModalCreateEvaluationStep1 } from './ModalCreateEvaluationStep1';
import { ModalCreateEvaluationStep2 } from './ModalCreateEvaluationStep2';
import { ModalCreateEvaluationStepQuestions } from './ModalCreateEvaluationStepQuestions';
import { handleWizardError } from 'utils/error-handler';
import { replaceTimeZone } from 'services/date';
import { useCallback, useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import type {
  IAssessmentKind,
  IProfessorAreaEvaluationAssessmentCreate,
  IProfessorAreaEvaluationsList
} from 'interfaces/professor_area/evaluations';
import type { IClassroomEvaluation } from 'interfaces/classrooms';
import type { SelectProps } from 'antd';

export type ModalCreateEvaluationMode = 'professor' | 'admin';

function buildAdminPayload(
  rest: Record<string, unknown>,
  accessType: string
): Record<string, unknown> {
  const base: Record<string, unknown> =
    accessType === 'fixed'
      ? {
          ...rest,
          starts_at: replaceTimeZone(rest.starts_at as Dayjs),
          ends_at: replaceTimeZone(rest.ends_at as Dayjs),
          start_after_days: rest.start_after_days ?? '0',
          duration_days: rest.duration_days ?? '0'
        }
      : {
          ...rest,
          start_after_days: rest.start_after_days ?? '0',
          duration_days: rest.duration_days ?? '0'
        };

  if (rest.kind === 'final') {
    delete base.weight;
    delete base.parent_id;
    delete base.rule;
  }

  return base;
}

const PROFESSOR_ASSESSMENT_FILTER: CrudFilters = [
  { field: 'filter[by_evaluation_type]', operator: 'eq', value: 'Assessment' }
];

const PROFESSOR_ASSIGNMENT_FILTER: CrudFilters = [
  { field: 'filter[by_evaluation_type]', operator: 'eq', value: 'Assignment' }
];

export interface ModalCreateEvaluationProps {
  /** Professor flow (default) or admin course-creation classroom. */
  mode?: ModalCreateEvaluationMode;
  modalProps: ModalProps;
  formProps?: FormProps<IProfessorAreaEvaluationAssessmentCreate>;
  classroomId: string;
  isLoading: boolean;
  onCloseModal: () => void;
  assessmentSelectProps: SelectProps;
  /** Required when `allowAssignmentType` is true (admin). */
  assignmentSelectProps?: SelectProps;
  /** Admin: Avaliação vs Atividade no passo 1. */
  allowAssignmentType?: boolean;
  /**
   * Resource for listing evaluations (prévia de pesos).
   * Defaults: professor → `professor_area/classrooms/:id/evaluations`,
   * admin → `classrooms/:id/evaluations`.
   */
  evaluationsResource?: string;
  /**
   * Filtros da listagem para a prévia.
   * Professor: default só Assessment. Admin: sem filtro extra se omitido.
   */
  listFilters?: CrudFilters | null;
  /** Quando a API devolve outro formato (ex.: admin), mapear para a prévia. */
  mapListItemToPreview?: (
    item: IClassroomEvaluation | IProfessorAreaEvaluationsList
  ) => IProfessorAreaEvaluationsList;
  modalTitle?: string;
  /**
   * Quando preenchido, o modal cria uma substitutiva para a avaliação
   * informada (kind=replacement, parent_id=esta, weight ausente,
   * passing_score e access_type herdados da avaliação pai).
   */
  replacementParent?: IProfessorAreaEvaluationsList;
}

export const ModalCreateEvaluation = ({
  mode = 'professor',
  modalProps,
  formProps,
  classroomId,
  isLoading,
  onCloseModal,
  assessmentSelectProps,
  assignmentSelectProps,
  allowAssignmentType = false,
  evaluationsResource: evaluationsResourceProp,
  listFilters: listFiltersProp,
  mapListItemToPreview,
  modalTitle: modalTitleProp,
  replacementParent
}: ModalCreateEvaluationProps) => {
  const isReplacement = Boolean(replacementParent);
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const [type, setType] = useState<'list' | 'new'>('list');
  const [assessmentKind, setAssessmentKind] =
    useState<IAssessmentKind | null>();
  const [formData, setFormData] =
    useState<IProfessorAreaEvaluationAssessmentCreate>(
      {} as IProfessorAreaEvaluationAssessmentCreate
    );

  const isNewAssessment =
    mode === 'professor' && type === 'new' && !isReplacement;

  const evaluationsResource =
    evaluationsResourceProp ??
    (mode === 'professor'
      ? 'professor_area/classroom_assessments'
      : `classrooms/${classroomId}/evaluations`);
  const evaluationsMeta = { classroomId };

  const isProfessorDefaultMode =
    mode === 'professor' && listFiltersProp === undefined;

  const resolvedListFilters = useMemo(() => {
    if (listFiltersProp === null) {
      return;
    }

    if (listFiltersProp !== undefined) {
      return listFiltersProp;
    }

    return mode === 'professor' ? PROFESSOR_ASSESSMENT_FILTER : undefined;
  }, [listFiltersProp, mode]);

  const modalIsOpen = Boolean(modalProps.open ?? modalProps.visible);

  const { data: classroomEvaluationsResult, isLoading: evaluationsLoading } =
    useList<IProfessorAreaEvaluationsList | IClassroomEvaluation, HttpError>({
      resource: evaluationsResource,
      meta: evaluationsMeta,
      pagination: {
        pageSize: 200
      },
      ...(resolvedListFilters && resolvedListFilters.length > 0
        ? { filters: resolvedListFilters }
        : {}),
      queryOptions: {
        enabled: Boolean(classroomId && modalIsOpen)
      }
    });

  const { data: assignmentsResult, isLoading: assignmentsLoading } = useList<
    IProfessorAreaEvaluationsList,
    HttpError
  >({
    resource: evaluationsResource,
    meta: evaluationsMeta,
    pagination: { pageSize: 200 },
    filters: PROFESSOR_ASSIGNMENT_FILTER,
    queryOptions: {
      enabled: Boolean(isProfessorDefaultMode && classroomId && modalIsOpen)
    }
  });

  const classroomEvaluations = useMemo(() => {
    const primary = classroomEvaluationsResult?.data ?? [];
    const secondary = isProfessorDefaultMode
      ? assignmentsResult?.data ?? []
      : [];
    const rows = [...primary, ...secondary];

    if (mapListItemToPreview) {
      return rows.map(row => mapListItemToPreview(row));
    }
    return rows as IProfessorAreaEvaluationsList[];
  }, [
    classroomEvaluationsResult?.data,
    assignmentsResult?.data,
    isProfessorDefaultMode,
    mapListItemToPreview
  ]);

  const combinedEvaluationsLoading =
    evaluationsLoading || (isProfessorDefaultMode && assignmentsLoading);

  const defaultTitle = (() => {
    if (isReplacement) {
      return `Adicionar substitutiva de: ${replacementParent?.name ?? ''}`;
    }
    return mode === 'admin'
      ? 'Criar nova avaliação'
      : 'Adicionar avaliação à turma';
  })();
  const modalTitle = modalTitleProp ?? defaultTitle;

  const initialFormValues = useMemo(() => {
    const values: Record<string, unknown> = { ...formData };

    if (mode === 'professor') {
      values.evaluation_type = 'Assessment';
    }

    if (isReplacement && replacementParent) {
      values.name = `Substitutiva - ${replacementParent.name}`;
    }
    return values;
  }, [formData, mode, isReplacement, replacementParent]);

  const steps = useMemo(
    () => [
      {
        title: 'Informações básicas',
        content: (
          <ModalCreateEvaluationStep1
            assessmentSelectProps={assessmentSelectProps}
            allowAssignmentType={Boolean(
              mode === 'admin' && allowAssignmentType
            )}
            assignmentSelectProps={assignmentSelectProps}
            isReplacement={isReplacement}
            replacementParent={replacementParent}
          />
        )
      },
      ...(isNewAssessment
        ? [
            {
              title: 'Questões',
              content: (
                <ModalCreateEvaluationStepQuestions
                  assessmentKind={assessmentKind ?? undefined}
                />
              )
            }
          ]
        : []),
      {
        title: 'Parâmetros',
        content: (
          <ModalCreateEvaluationStep2
            classroomEvaluations={classroomEvaluations}
            evaluationsLoading={combinedEvaluationsLoading}
            isReplacement={isReplacement}
          />
        )
      }
    ],
    [
      assessmentSelectProps,
      mode,
      allowAssignmentType,
      assignmentSelectProps,
      isReplacement,
      replacementParent,
      isNewAssessment,
      assessmentKind,
      classroomEvaluations,
      combinedEvaluationsLoading
    ]
  );

  const buildProfessorPayload = useCallback(
    (rest: Record<string, unknown>) => {
      const {
        type: _type,
        time_in_minutes,
        ...restWithoutType
      } = rest as Record<string, unknown> & { time_in_minutes?: number };

      const timeInSeconds =
        time_in_minutes !== undefined && time_in_minutes !== null
          ? Number(time_in_minutes) * 60
          : undefined;

      const baseData = {
        ...(restWithoutType as Omit<
          IProfessorAreaEvaluationAssessmentCreate,
          'classroom_id'
        >),
        evaluation_type: isReplacement
          ? (rest.evaluation_type as 'Assessment' | 'Assignment') ??
            'Assessment'
          : ('Assessment' as const),
        classroom_id: classroomId,
        ...(timeInSeconds !== undefined
          ? { time_in_seconds: timeInSeconds }
          : {}),
        ...(isReplacement
          ? {
              kind: 'replacement' as const,
              parent_id: replacementParent?.id,
              weight: undefined,
              access_type: 'fixed',
              passing_score: replacementParent?.passing_score
            }
          : {})
      };

      const payload = isNewAssessment
        ? { ...baseData, evaluation_id: undefined }
        : baseData;

      if (rest.kind === 'final') {
        const data = payload as unknown as Record<string, unknown>;
        delete data.weight;
        delete data.parent_id;
        delete data.rule;
      }

      return payload;
    },
    [
      classroomId,
      isNewAssessment,
      isReplacement,
      replacementParent?.id,
      replacementParent?.passing_score
    ]
  );

  const onFinish = useCallback(async () => {
    try {
      await form.validateFields();
    } catch (error: unknown) {
      handleWizardError(form, error);
      return;
    }

    const dataForm = { ...formData, ...form.getFieldsValue() } as Record<
      string,
      unknown
    >;
    const { optional_evaluation: _optionalEvaluation, ...rest } = dataForm;

    let payload: IProfessorAreaEvaluationAssessmentCreate;

    if (mode === 'professor') {
      payload = buildProfessorPayload(
        rest
      ) as IProfessorAreaEvaluationAssessmentCreate;
    } else {
      const accessType = (rest.access_type as string | undefined) ?? 'fixed';

      if (
        accessType === 'fixed' &&
        (rest.starts_at == null || rest.ends_at == null)
      ) {
        notification.warning({
          message: 'Período incompleto',
          description:
            'Informe data e hora de início e de término do período de acesso.'
        });
        return;
      }

      payload = buildAdminPayload(
        rest,
        accessType
      ) as unknown as IProfessorAreaEvaluationAssessmentCreate;
    }

    try {
      await formProps?.onFinish?.(payload);
      onCloseModal();
    } catch {
      // Reported by refine's errorNotification; keep the modal open so the
      // user can correct and retry.
    }
  }, [form, formData, formProps, onCloseModal, mode, buildProfessorPayload]);

  const changeStep = async (step: number, validate?: boolean) => {
    try {
      if (validate) {
        await form.validateFields();
      }

      const currentValues = form.getFieldsValue();

      setFormData(prev => ({ ...prev, ...currentValues }));
      setCurrent(step);
    } catch (error: unknown) {
      handleWizardError(form, error);
    }
  };

  return (
    <Modal
      {...modalProps}
      title={modalTitle}
      width={900}
      style={{ top: 20, paddingBottom: 20 }}
      centered
      maskClosable={false}
      afterClose={() => {
        form.resetFields();
        setCurrent(0);
        setType('list');
        setAssessmentKind(null);
        setFormData({} as IProfessorAreaEvaluationAssessmentCreate);
      }}
      footer={
        <Space className="justify-end w-full">
          {current > 0 && (
            <Button
              icon={<LeftOutlined />}
              onClick={() => changeStep(current - 1)}
            >
              Voltar
            </Button>
          )}

          {current < steps.length - 1 && (
            <Button
              type="primary"
              icon={<RightOutlined />}
              iconPosition="end"
              onClick={() => changeStep(current + 1, true)}
            >
              Próximo
            </Button>
          )}

          {current === steps.length - 1 && (
            <Button
              htmlType="submit"
              type="primary"
              loading={isLoading}
              onClick={onFinish}
            >
              Criar avaliação
            </Button>
          )}
        </Space>
      }
    >
      <div className="grid grid-cols-[240px_1fr] divide-x">
        <Steps
          current={current}
          items={steps}
          className="h-fit overflow-y-auto [&_.ant-steps-item-finish_.ant-steps-item-icon]:bg-primary-500/10"
          direction="vertical"
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={initialFormValues}
          onValuesChange={changedValues => {
            if (changedValues.type) {
              setType(changedValues.type);
            }

            if (changedValues.assessment_kind !== undefined) {
              setAssessmentKind(changedValues.assessment_kind ?? null);
              form.setFieldValue('questions', []);
            }
          }}
          className="pl-6"
        >
          {steps[current].content}
        </Form>
      </div>
    </Modal>
  );
};
